import { useState, useEffect, useCallback, useMemo } from "react";
import { sqlReadRaw as sqlRead, payloadOf } from "../../../utils/sqlClient";
import PanelsCircuitsPanel from "./PanelsCircuitsPanel";
import ElecLineItemsPanel from "./ElecLineItemsPanel";
import ElecSettingsRollupPanel from "./ElecSettingsRollupPanel";
import { buildBodOptionAttrs, DEFAULT_SYSTEMS_TUNABLES } from "../../../utils/roomSystemsEngine";
import { groupElementsByRoom } from "../../../config/roomElements";

// Electrical Estimate — panels/circuits auto-generated from the Room Takeoff
// + elec discipline criteria (src/utils/electricalCircuitEngine.js), priced
// against a reusable Category/Item/Assembly/Formula library using a
// sequential commercial stack reverse-engineered from the Strive AZ workbook
// (src/utils/estimatePricing.js). Revision-keyed like Room Takeoff/Scope
// Register; the panel/circuit backend itself (db/24_process_equipment.sql)
// predates this page — this is its first frontend. Equipment
// (ProcessEquipment/EquipmentConnections) CRUD is a separate, already-scoped
// feature — read here only to feed circuit generation and cost rollup.

const TABS = [
  { key: "panels",     label: "Panels & Circuits" },
  { key: "lineItems",  label: "Line Items" },
  { key: "settings",   label: "Settings & Rollup" },
];

export default function ElectricalEstimate({ projectId }) {
  const [revisions, setRevisions]     = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState("panels");

  const [rooms, setRooms]         = useState([]);
  const [criteria, setCriteria]   = useState({});
  const [equipment, setEquipment] = useState([]);
  const [connections, setConnections] = useState([]);
  const [panels, setPanels]       = useState([]);
  const [circuits, setCircuits]   = useState([]);
  const [categories, setCategories]     = useState([]);
  const [assemblies, setAssemblies]     = useState([]);
  const [items, setItems]               = useState([]);
  const [laborFactors, setLaborFactors] = useState([]);
  const [formulas, setFormulas]         = useState([]);
  const [lineItems, setLineItems]       = useState([]);
  const [settings, setSettings]   = useState(null);
  const [roomTemplateCriteria, setRoomTemplateCriteria] = useState([]); // Room Templates catalog (global) — see db/55_room_templates.sql
  const [bodOptionRows, setBodOptionRows] = useState([]);           // Basis of Design option rows (global) — fixture lumens/VA for the lighting engine
  const [takeoffVariableRows, setTakeoffVariableRows] = useState([]); // Takeoff Variables (global) — lighting CU/LLF/spacing assumptions (db/68)

  // Global reference data the lighting engine (src/utils/roomSystemsEngine.js)
  // reads when circuit generation asks for a room's fixture count/VA —
  // loaded once, same convention as RoomTakeoff.jsx's own fetches.
  useEffect(() => {
    sqlRead("EXEC sp_GetBasisOfDesignOptions")
      .then(result => setBodOptionRows(result?.Table1 || []))
      .catch(e => console.error("Basis of Design options load failed (lighting engine uses defaults):", e));
    sqlRead("EXEC sp_GetTakeoffVariables")
      .then(result => setTakeoffVariableRows(result?.Table1 || []))
      .catch(e => console.error("Takeoff variables load failed (lighting engine uses defaults):", e));
  }, []);
  // Room Build-Up gating (db/70): a room's lighting circuits exist only when
  // the room carries a lighting element — same rule the Arch Takeoff applies.
  const [roomElements, setRoomElements] = useState([]);
  useEffect(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetRoomElements @revision_id=${activeRevId}`)
      .then(result => setRoomElements(result?.Table1 || []))
      .catch(e => console.error("Room elements load failed (lighting engine ungated):", e));
  }, [activeRevId]);
  // Placed symbols — a receptacle element with QuantityMethod='placed' counts
  // its canvas symbols (roomElements.js placedCountFor); a load failure leaves
  // such an element at 0 placed, never at the legacy field.
  const [takeoffConditions, setTakeoffConditions] = useState([]);
  useEffect(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetTakeoffConditionsDrawn @revision_id=${activeRevId}`)
      .then(result => setTakeoffConditions(result?.Table1 || []))
      .catch(e => console.error("Placed conditions load failed (placed receptacle elements count 0):", e));
  }, [activeRevId]);
  const systemsCtx = useMemo(() => ({
    bodAttrs: buildBodOptionAttrs(bodOptionRows),
    tunables: { ...DEFAULT_SYSTEMS_TUNABLES, ...Object.fromEntries(takeoffVariableRows.map(r => [r.VariableKey, +r.Value])) },
    roomElementsByRoom: groupElementsByRoom(roomElements),
    takeoffConditions,   // receptacle elements counted from the canvas (electricalCircuitEngine.js receptacleCount)
    formulas,            // a receptacle element carrying a QuantityFormulaID
  }), [bodOptionRows, takeoffVariableRows, roomElements, takeoffConditions, formulas]);

  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        setRevisions(revs);
        const active = revs.find(r => !r.IsLocked) || revs[0];
        if (active) setActiveRevId(active.RevisionID);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadTakeoff = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetRoomTakeoff @revision_id=${activeRevId}`)
      .then(result => {
        setRooms(result?.Table3 || []);
        const cr = {};
        for (const c of result?.Table6 || []) cr[`${c.Discipline}|${c.FieldKey}|${c.RoomID ?? 0}`] = c.FieldValue;
        setCriteria(cr);
      })
      .catch(e => console.error("Room takeoff load failed:", e));
  }, [activeRevId]);

  // Global (not revision-scoped), same as sp_GetEstimateCostLibrary below —
  // feeds each room's _templateDefaults (see roomsWithTemplateDefaults),
  // effectiveCriterion()'s room-template fallback tier for outlet_count/
  // lighting_fixture_count (electricalCircuitEngine.js).
  const loadRoomTemplates = useCallback(() => {
    sqlRead("EXEC sp_GetRoomTemplates")
      .then(result => setRoomTemplateCriteria(result?.Table2 || []))
      .catch(e => console.error("Room Templates load failed (template defaults unavailable):", e));
  }, []);

  const loadEquipmentRegister = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEquipmentRegister @revision_id=${activeRevId}`)
      .then(result => {
        setEquipment(result?.Table1 || []);
        setConnections(result?.Table2 || []);
        setPanels(result?.Table3 || []);
        setCircuits(result?.Table4 || []);
      })
      .catch(e => console.error("Equipment register load failed:", e));
  }, [activeRevId]);

  const loadLibrary = useCallback(() => {
    sqlRead(`EXEC sp_GetEstimateCostLibrary`)
      .then(result => {
        setCategories(result?.Table1 || []);
        setAssemblies(result?.Table2 || []);
        setItems(result?.Table3 || []);
        setLaborFactors(result?.Table4 || []);
        setFormulas(result?.Table5 || []);
      })
      .catch(e => console.error("Electrical library load failed:", e));
  }, []);

  const loadLineItems = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateCostLineItems @revision_id=${activeRevId}`)
      .then(result => setLineItems(result?.Table1 || []))
      .catch(e => console.error("Electrical line items load failed:", e));
  }, [activeRevId]);

  const loadSettings = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateCostSettings @revision_id=${activeRevId}`)
      .then(result => setSettings((result?.Table1 || [])[0] || null))
      .catch(e => console.error("Electrical settings load failed:", e));
  }, [activeRevId]);

  useEffect(() => { loadTakeoff(); loadEquipmentRegister(); loadLibrary(); loadLineItems(); loadSettings(); loadRoomTemplates(); },
    [loadTakeoff, loadEquipmentRegister, loadLibrary, loadLineItems, loadSettings, loadRoomTemplates]);

  // Same "_cfm"-style attach-on-room-object convention as RoomTakeoff.jsx's
  // own roomsWithTemplateDefaults — see that file and takeoffCriteria.js's
  // effectiveCriterion() for why this isn't a new function parameter.
  const templateDefaultsByTemplateId = useMemo(() => {
    const map = {};
    for (const c of roomTemplateCriteria) {
      (map[c.RoomTemplateID] ||= {})[`${c.Discipline}|${c.FieldKey}`] = c.FieldValue;
    }
    return map;
  }, [roomTemplateCriteria]);
  const roomsWithTemplateDefaults = useMemo(
    () => rooms.map(r => ({ ...r, _templateDefaults: r.RoomTemplateID ? (templateDefaultsByTemplateId[r.RoomTemplateID] || null) : null })),
    [rooms, templateDefaultsByTemplateId]
  );

  const activeRev = revisions.find(r => r.RevisionID === activeRevId);
  const isLocked = !!activeRev?.IsLocked;

  if (loading) return <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;

  if (!revisions.length) return (
    <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>
      No estimate revision yet — create one on the Revisions tab first.
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, marginRight: "auto" }}>⚡ Electrical Estimate</h2>
        <select
          value={activeRevId || ""}
          onChange={e => setActiveRevId(+e.target.value)}
          style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--cs-border)" }}
        >
          {revisions.map(r => (
            <option key={r.RevisionID} value={r.RevisionID}>
              Rev {r.RevisionNumber} {r.RevisionLabel ? `— ${r.RevisionLabel}` : ""}{r.IsLocked ? " (locked)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cs-border)", marginBottom: 16 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 14px", fontSize: 13, fontWeight: 600, background: "none", border: "none",
              cursor: "pointer", color: tab === t.key ? "var(--cs-blue)" : "var(--text3)",
              borderBottom: tab === t.key ? "2px solid var(--cs-blue)" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLocked && (
        <div style={{ fontSize: 12, color: "#92400e", background: "var(--tint-amber)", padding: "6px 12px", borderRadius: 6, marginBottom: 12 }}>
          This revision is locked — read-only.
        </div>
      )}

      {tab === "panels" && (
        <PanelsCircuitsPanel
          activeRevId={activeRevId} isLocked={isLocked} payloadOf={payloadOf}
          rooms={roomsWithTemplateDefaults} criteria={criteria} equipment={equipment} connections={connections}
          panels={panels} circuits={circuits} assemblies={assemblies} formulas={formulas}
          settings={settings} onReload={loadEquipmentRegister}
          systemsCtx={systemsCtx}
        />
      )}
      {tab === "lineItems" && (
        <ElecLineItemsPanel
          activeRevId={activeRevId} isLocked={isLocked} payloadOf={payloadOf}
          lineItems={lineItems} items={items} assemblies={assemblies} categories={categories}
          laborFactors={laborFactors} formulas={formulas} settings={settings}
          onReload={loadLineItems}
        />
      )}
      {tab === "settings" && (
        <ElecSettingsRollupPanel
          activeRevId={activeRevId} isLocked={isLocked} payloadOf={payloadOf}
          settings={settings} onReload={loadSettings} formulas={formulas}
          circuits={circuits} connections={connections.filter(c => c.UtilityType === "electrical")} panels={panels} lineItems={lineItems}
          assemblies={assemblies} items={items} categories={categories} laborFactors={laborFactors}
        />
      )}
    </div>
  );
}
